/**
 * "Quality is waiting on you" — the OPEN asks Merlin sent this person for
 * the quality desk, shown where they already work (kanban 2a28d4c0): above
 * the inbox list, at the top of Customers, and on "Questions for me".
 *
 * Each row: Answer (inline, sends a portal answer), Open the item (only for
 * people who can open the quality door), Not mine (a note back to the desk).
 * Hidden when there is nothing to answer, unless `showEmpty` (the page).
 * Every control records a trackEvent row with the ask's ids.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { trackEvent } from '../../../services/portalTracking';
import { answerMyAsk, getMyAsks, passAskBack, type MyAsk } from '../../../services/portalQualityAsks';
import {
  askCustomerLabel,
  askedLine,
  askItemPath,
  askOneLine,
  askReference,
  askTrackIds,
} from './askCardView';
import styles from './QualityAsksCard.module.css';

export type AskCardSurface = 'inbox' | 'customers' | 'questions_for_me';

interface QualityAsksCardProps {
  title: string;
  surface: AskCardSurface;
  /** Render the card with an empty-state line instead of hiding it. */
  showEmpty?: boolean;
  /** Show the "· also under More › Questions for me" pointer. */
  showQuestionsLink?: boolean;
}

type Mode = { kind: 'idle' } | { kind: 'answer'; id: string } | { kind: 'not_mine'; id: string };

export default function QualityAsksCard({ title, surface, showEmpty = false, showQuestionsLink = false }: QualityAsksCardProps) {
  const navigate = useNavigate();
  const { hasScope, user } = useAuth();
  // Only people who can open the quality door get a link into it.
  const canOpenItems = Boolean(hasScope?.('quality:copilot')) || user?.role === 'ADMIN';
  const [asks, setAsks] = useState<MyAsk[] | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setAsks(await getMyAsks());
    } catch {
      setAsks([]); // never break the host page
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (asks === null) return null;
  if (asks.length === 0 && !showEmpty && !done) return null;

  const start = (next: Mode, ask: MyAsk) => {
    setMode(next);
    setDraft('');
    setError(null);
    setDone(null);
    trackEvent('action', next.kind === 'answer' ? 'ask_answer_open' : 'ask_not_mine', {
      ...askTrackIds(ask),
      surface,
      outcome: 'panel_open',
    });
  };

  const cancel = () => {
    setMode({ kind: 'idle' });
    setDraft('');
    setError(null);
  };

  const finish = (ask: MyAsk, message: string) => {
    setAsks((prev) => (prev ?? []).filter((a) => a.id !== ask.id));
    setMode({ kind: 'idle' });
    setDraft('');
    setDone(message);
  };

  const send = async (ask: MyAsk) => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      await answerMyAsk(ask.id, text);
      trackEvent('action', 'ask_answer_send', { ...askTrackIds(ask), surface, outcome: 'request_sent' });
      finish(ask, `Sent. ${ask.asked_for} has your answer.`);
    } catch {
      trackEvent('action', 'ask_answer_send', { ...askTrackIds(ask), surface, outcome: 'error' });
      setError('That did not go through. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const confirmNotMine = async (ask: MyAsk) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await passAskBack(ask.id, draft.trim());
      trackEvent('action', 'ask_not_mine', { ...askTrackIds(ask), surface, outcome: 'request_sent' });
      finish(ask, `Passed back to ${ask.asked_for}. Quality will find the right person.`);
    } catch {
      trackEvent('action', 'ask_not_mine', { ...askTrackIds(ask), surface, outcome: 'error' });
      setError('That did not go through. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const openItem = (ask: MyAsk, path: string) => {
    trackEvent('action', 'ask_open_item', { ...askTrackIds(ask), surface, outcome: 'navigate' });
    navigate(path);
  };

  return (
    <section className={styles.card} aria-label={title} data-testid="quality-asks-card">
      <div className={styles.head}>
        <h2 className={styles.title}>{title}</h2>
        <span className={styles.count} aria-label={`${asks.length} open`}>{asks.length}</span>
        {showQuestionsLink && (
          <span className={styles.pointer}>
            · also under More ›{' '}
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => {
                trackEvent('action', 'ask_questions_page_open', { surface, outcome: 'navigate' });
                navigate('/portal/questions-for-me');
              }}
            >
              Questions for me
            </button>
          </span>
        )}
      </div>
      {done && <div className={styles.done} role="status">{done}</div>}
      {asks.length === 0 && showEmpty && (
        <div className={styles.empty}>Nothing from quality is waiting on you.</div>
      )}
      {asks.map((ask) => {
        const customer = askCustomerLabel(ask);
        const reference = askReference(ask);
        const path = canOpenItems ? askItemPath(ask) : null;
        const answering = mode.kind === 'answer' && mode.id === ask.id;
        const passing = mode.kind === 'not_mine' && mode.id === ask.id;
        return (
          <div key={ask.id} className={styles.row} data-testid="quality-ask-row">
            <div className={styles.rowMain}>
              <div className={styles.rowText}>
                <div className={styles.subject}>
                  <strong>{ask.question_title}</strong>
                  {customer && <> — {customer}</>}
                  {reference && <span className={styles.muted}> · {reference}</span>}
                </div>
                <div className={styles.meta}>
                  {askedLine(ask)} · “{askOneLine(ask.question_body)}”
                </div>
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={() => (answering ? cancel() : start({ kind: 'answer', id: ask.id }, ask))}
                  aria-expanded={answering}
                >
                  Answer
                </button>
                {path && (
                  <button type="button" className={styles.btn} onClick={() => openItem(ask, path)}>
                    Open the item
                  </button>
                )}
                <button
                  type="button"
                  className={styles.btn}
                  onClick={() => (passing ? cancel() : start({ kind: 'not_mine', id: ask.id }, ask))}
                  aria-expanded={passing}
                >
                  Not mine
                </button>
              </div>
            </div>
            {(answering || passing) && (
              <div className={styles.panel}>
                <label className={styles.panelLabel} htmlFor={`ask-${ask.id}`}>
                  {answering
                    ? `Your answer for ${ask.asked_for}`
                    : 'Who should quality ask instead? (optional)'}
                </label>
                <textarea
                  id={`ask-${ask.id}`}
                  className={styles.textarea}
                  rows={answering ? 3 : 2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  autoFocus
                />
                {error && <div className={styles.error} role="alert">{error}</div>}
                <div className={styles.panelActions}>
                  {answering ? (
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={() => void send(ask)}
                      disabled={busy || !draft.trim()}
                    >
                      {busy ? 'Sending…' : 'Send'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={() => void confirmNotMine(ask)}
                      disabled={busy}
                    >
                      {busy ? 'Sending…' : 'Confirm: not mine'}
                    </button>
                  )}
                  <button type="button" className={styles.btn} onClick={cancel} disabled={busy}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
